import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Typography, Link as MuiLink, Divider } from '@mui/material';

/**
 * Footer component with copyright and links
 * @returns {JSX.Element} The Footer component
 */
const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Bazni URL za eksterni sajt
  const baseUrl = 'https://consciencegems.ch';

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 'auto',
        backgroundColor: (theme) => theme.palette.background.default,
        borderTop: '1px solid',
        borderColor: (theme) => theme.palette.primary.main,
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            sm={4}
          >
            <Typography
              variant="h6"
              color="text.primary"
              fontFamily="'Playfair Display', serif"
              fontWeight={600}
              mb={1}
            >
              Conscience SA
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {t('common.tagline')}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
          >
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
            >
              {t('navigation.links')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
            >
              <MuiLink
                component={Link}
                to="/products"
                color="inherit"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {t('navigation.products')}
              </MuiLink>
            </Typography>
            <Typography
              variant="body2"
              component="p"
            >
              <MuiLink
                href={`${baseUrl}/a-propos`}
                color="inherit"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {t('navigation.about')}
              </MuiLink>
            </Typography>
            <Typography
              variant="body2"
              component="p"
            >
              <MuiLink
                href={`${baseUrl}/contact`}
                color="inherit"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {t('navigation.contact')}
              </MuiLink>
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
          >
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
            >
              {t('navigation.legal')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
            >
              <MuiLink
                href={`${baseUrl}/privacy`}
                color="inherit"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {t('navigation.privacy')}
              </MuiLink>
            </Typography>
            <Typography
              variant="body2"
              component="p"
            >
              <MuiLink
                href={`${baseUrl}/terms`}
                color="inherit"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {t('navigation.terms')}
              </MuiLink>
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: 'primary.main' }} />

        <Box mt={2}>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
          >
            {'© '}
            {currentYear}
            {' Conscience SA. '}
            {t('common.allRightsReserved')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
